import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    GripVertical,
    ExternalLink,
    Link,
    Tag,
    FileText,
    ShoppingBag,
    LayoutList,
    ChevronRight,
    Image as ImageIcon,
    X,
    Save,
} from 'lucide-react';
import Notification from '../Common/Notification';
import { storefrontGet, storefrontPost, storefrontPut, storefrontDelete } from '../../services/storefrontApi';
import AppleSelect from '../Common/AppleSelect';
import {tabClass} from '../Common/TabBar';
import ActionMenu from '../Common/ActionMenu';

/* ─── Quill config ──────────────────────────────────────────────────────── */
const QUILL_MODULES = {
    toolbar: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'blockquote'],
        ['clean'],
    ],
};
const QUILL_FORMATS = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'blockquote',
];

/* Inject dark-mode overrides for Quill once */
const QUILL_DARK_STYLE_ID = 'quill-dark-nav';
if (!document.getElementById(QUILL_DARK_STYLE_ID)) {
    const s = document.createElement('style');
    s.id = QUILL_DARK_STYLE_ID;
    s.textContent = `
        .quill-nav-wrapper .ql-toolbar {
            border: none !important;
            border-bottom: 1px solid #374151 !important;
            background: #1f2937;
        }
        .quill-nav-wrapper .ql-container {
            border: none !important;
            background: #111827;
            min-height: 140px;
            font-size: 14px;
        }
        .quill-nav-wrapper .ql-editor {
            color: #e5e7eb;
            min-height: 140px;
        }
        .quill-nav-wrapper .ql-editor.ql-blank::before {
            color: #6b7280;
            font-style: italic;
        }
        .quill-nav-wrapper .ql-toolbar .ql-stroke { stroke: #9ca3af; }
        .quill-nav-wrapper .ql-toolbar .ql-fill   { fill:   #9ca3af; }
        .quill-nav-wrapper .ql-toolbar .ql-picker  { color:  #9ca3af; }
        .quill-nav-wrapper .ql-toolbar button:hover .ql-stroke,
        .quill-nav-wrapper .ql-toolbar button.ql-active .ql-stroke { stroke: #4DB6AC; }
        .quill-nav-wrapper .ql-toolbar button:hover .ql-fill,
        .quill-nav-wrapper .ql-toolbar button.ql-active .ql-fill   { fill:   #4DB6AC; }
        /* Light mode */
        :not(.dark) .quill-nav-wrapper .ql-toolbar {
            background: #f9fafb;
            border-bottom-color: #e5e7eb !important;
        }
        :not(.dark) .quill-nav-wrapper .ql-container { background: #fff; }
        :not(.dark) .quill-nav-wrapper .ql-editor { color: #111827; }
        :not(.dark) .quill-nav-wrapper .ql-toolbar .ql-stroke { stroke: #4b5563; }
        :not(.dark) .quill-nav-wrapper .ql-toolbar .ql-fill   { fill:   #4b5563; }
        :not(.dark) .quill-nav-wrapper .ql-toolbar .ql-picker  { color:  #4b5563; }
    `;
    document.head.appendChild(s);
}

/* ─── Location / Type config ─────────────────────────────────────────────── */
const LOCATIONS = [
    { key: 'header',  label: 'En-tête' },
    { key: 'footer',  label: 'Pied de page' },
    { key: 'sidebar', label: 'Barre latérale' },
];

const TYPES = [
    { key: 'link',       label: 'Lien interne',  icon: Link,         hasContent: false },
    { key: 'external',   label: 'Lien externe',  icon: ExternalLink, hasContent: false },
    { key: 'page',       label: 'Page',           icon: FileText,     hasContent: true  },
    { key: 'category',   label: 'Catégorie',      icon: Tag,          hasContent: false },
    { key: 'product',    label: 'Produit',         icon: ShoppingBag,  hasContent: false },
    { key: 'menu',       label: 'Menu',            icon: LayoutList,   hasContent: false },
];

const typeInfo = (type) => TYPES.find(t => t.key === type) || TYPES[0];

const typeIcon = (type) => {
    const Icon = typeInfo(type).icon;
    return <Icon size={14} className="text-gray-400 flex-shrink-0" />;
};

/* ─── Toggle helper ─────────────────────────────────────────────────────── */
const Toggle = ({ value, onChange, label }) => (
    <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
        <button
            type="button"
            onClick={() => onChange(!value)}
            className={`relative h-6 w-11 rounded-full transition-colors ${value ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
        >
            <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);

/* ─── Editor modal ──────────────────────────────────────────────────────── */
const NavEditor = ({ item, items, onClose, onSave, isSaving }) => {
    const isEdit = !!item?.id;
    const [form, setForm] = useState({
        label:           item?.label           || '',
        url:             item?.url             || '',
        type:            item?.type            || 'link',
        location:        item?.location        || 'header',
        icon:            item?.icon            || '',
        parent_id:       item?.parent_id       || '',
        position:        item?.position        ?? 0,
        active:          item?.active          ?? true,
        open_in_new_tab: item?.open_in_new_tab || false,
        // page-content fields (new DB columns)
        description:     item?.description     || '',
        image:           item?.image           || '',
    });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const parents = items.filter(i => !i.parent_id && i.id !== item?.id);

    const isPageType    = form.type === 'page';
    const urlRequired   = !isPageType; // pages can exist without a URL slug
    const saveDisabled  = isSaving || !form.label || (urlRequired && !form.url);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {isEdit ? 'Modifier l\'élément' : 'Nouvel élément de navigation'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-5 space-y-4 max-h-[78vh] overflow-y-auto">

                    {/* Label */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Libellé *</label>
                        <input
                            type="text"
                            value={form.label}
                            onChange={e => set('label', e.target.value)}
                            placeholder="Nos Produits"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                        />
                    </div>

                    {/* Type + Location */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Type</label>
                            <AppleSelect
                                value={form.type}
                                onChange={e => set('type', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                            >
                                {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </AppleSelect>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Emplacement</label>
                            <AppleSelect
                                value={form.location}
                                onChange={e => set('location', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                            >
                                {LOCATIONS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                            </AppleSelect>
                        </div>
                    </div>

                    {/* URL — always shown; labelled optional for page type */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                            URL {urlRequired ? '*' : <span className="text-gray-400 font-normal">(optionnel pour les pages)</span>}
                        </label>
                        <input
                            type="text"
                            value={form.url}
                            onChange={e => set('url', e.target.value)}
                            placeholder={isPageType ? '/a-propos' : '/products'}
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                        />
                    </div>

                    {/* ── PAGE CONTENT SECTION ─────────────────────────────── */}
                    {isPageType && (
                        <div className="rounded-xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 space-y-4">
                            <p className="text-xs font-semibold text-primary uppercase tracking-wide flex items-center gap-1.5">
                                <FileText size={13} /> Contenu de la page
                            </p>

                            {/* Image URL */}
                            <div>
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block flex items-center gap-1.5">
                                    <ImageIcon size={12} /> Image (URL)
                                </label>
                                <input
                                    type="url"
                                    value={form.image}
                                    onChange={e => set('image', e.target.value)}
                                    placeholder="https://cdn.example.com/banner.jpg"
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                                />
                                {form.image && (
                                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 h-28">
                                        <img
                                            src={form.image}
                                            alt="Aperçu"
                                            className="w-full h-full object-cover"
                                            onError={e => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Description — WYSIWYG */}
                            <div>
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                                    Description
                                </label>
                                <div className="quill-nav-wrapper rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
                                    <ReactQuill
                                        theme="snow"
                                        value={form.description}
                                        onChange={v => set('description', v)}
                                        placeholder="Décrivez le contenu de cette page..."
                                        modules={QUILL_MODULES}
                                        formats={QUILL_FORMATS}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* ─────────────────────────────────────────────────────── */}

                    {/* Parent + Position */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Élément parent</label>
                            <AppleSelect
                                value={form.parent_id}
                                onChange={e => set('parent_id', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                            >
                                <option value="">— Aucun —</option>
                                {parents.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </AppleSelect>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Position</label>
                            <input
                                type="number"
                                min={0}
                                value={form.position}
                                onChange={e => set('position', parseInt(e.target.value, 10) || 0)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                            />
                        </div>
                    </div>

                    {/* Icon */}
                    <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Icône (nom Lucide)</label>
                        <input
                            type="text"
                            value={form.icon}
                            onChange={e => set('icon', e.target.value)}
                            placeholder="shopping-bag"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary"
                        />
                    </div>

                    {/* Toggles */}
                    <div className="space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                        <Toggle value={form.active}          onChange={v => set('active', v)}          label="Actif" />
                        <Toggle value={form.open_in_new_tab} onChange={v => set('open_in_new_tab', v)} label="Ouvrir dans un nouvel onglet" />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={saveDisabled}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Nav item row ──────────────────────────────────────────────────────── */
const NavRow = ({ item, onEdit, onDelete }) => {
    const isPage = item.type === 'page';
    return (
        <div className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border rounded-xl group transition-opacity ${!item.active ? 'opacity-50' : ''} border-gray-200 dark:border-gray-700`}>
            <GripVertical size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0 cursor-grab" />
            {typeIcon(item.type)}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{item.label}</p>
                    {isPage && item.image && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                            img
                        </span>
                    )}
                </div>
                <p className="text-xs text-gray-400 truncate">
                    {item.url || (isPage ? <span className="italic text-gray-300">pas d'URL</span> : '')}
                </p>
                {isPage && item.description && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate italic">
                        {item.description.slice(0, 60)}{item.description.length > 60 ? '…' : ''}
                    </p>
                )}
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${item.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {item.active ? 'Actif' : 'Inactif'}
            </span>
            <ActionMenu
                label={`Actions pour ${item.label || ''}`}
                items={[
                    {label: 'Modifier', icon: Pencil, onClick: () => onEdit(item)},
                    {divider: true},
                    {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(item)},
                ]}
            />
        </div>
    );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const NavigationSettings = () => {
    const [activeLocation, setActiveLocation] = useState('header');
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editorItem, setEditorItem] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type !== 'loading') setTimeout(() => setNotification(n => ({ ...n, show: false })), 3000);
    };

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await storefrontGet('/navigation', { location: activeLocation });
            const data = res.data?.data ?? res.data ?? [];
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            showNotification('error', 'Impossible de charger la navigation.');
        } finally {
            setIsLoading(false);
        }
    }, [activeLocation]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (form) => {
        setIsSaving(true);
        try {
            // Only send description/image when type is 'page'; null-out otherwise
            const payload = {
                ...form,
                description: form.type === 'page' ? (form.description || null) : null,
                image:       form.type === 'page' ? (form.image       || null) : null,
                parent_id:   form.parent_id || null,
            };
            if (editorItem?.id) {
                await storefrontPut(`/navigation/${editorItem.id}`, payload);
                showNotification('success', 'Élément mis à jour.');
            } else {
                await storefrontPost('/navigation', payload);
                showNotification('success', 'Élément créé.');
            }
            setEditorItem(undefined);
            load();
        } catch (err) {
            console.error(err);
            showNotification('error', 'Erreur lors de l\'enregistrement.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Supprimer « ${item.label} » ?`)) return;
        try {
            await storefrontDelete(`/navigation/${item.id}`);
            showNotification('success', 'Élément supprimé.');
            load();
        } catch {
            showNotification('error', 'Impossible de supprimer cet élément.');
        }
    };

    const topLevel = items.filter(i => !i.parent_id);
    const children = (parentId) => items.filter(i => i.parent_id === parentId);

    return (
        <div className="px-4 py-6 max-w-3xl mx-auto">
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification(n => ({ ...n, show: false }))}
            />

            {/* Location tabs */}
            <div className="flex overflow-x-auto no-scrollbar mb-6 border-b border-gray-200 dark:border-gray-700">
                {LOCATIONS.map(loc => (
                    <button
                        key={loc.key}
                        onClick={() => setActiveLocation(loc.key)}
                        className={`${tabClass(activeLocation === loc.key)} -mb-px`}
                    >
                        {loc.label}
                    </button>
                ))}
            </div>

            {/* Toolbar */}
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">{items.length} élément(s)</p>
                <button
                    onClick={() => setEditorItem(null)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors"
                >
                    <Plus size={16} /> Ajouter un élément
                </button>
            </div>

            {/* List */}
            {isLoading ? (
                <div className="flex justify-center py-12">
                    <RefreshCw size={20} className="animate-spin text-primary" />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                    <Link size={32} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Aucun élément pour cet emplacement.</p>
                    <button onClick={() => setEditorItem(null)} className="mt-3 text-primary text-sm hover:underline">
                        Ajouter le premier élément
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {topLevel
                        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                        .map(item => (
                            <div key={item.id}>
                                <NavRow item={item} onEdit={setEditorItem} onDelete={handleDelete} />
                                {children(item.id).length > 0 && (
                                    <div className="ml-8 mt-1.5 space-y-1.5">
                                        {children(item.id)
                                            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                                            .map(child => (
                                                <div key={child.id} className="flex items-center gap-2">
                                                    <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                                                    <div className="flex-1">
                                                        <NavRow item={child} onEdit={setEditorItem} onDelete={handleDelete} />
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            )}

            {editorItem !== undefined && (
                <NavEditor
                    item={editorItem}
                    items={items}
                    onClose={() => setEditorItem(undefined)}
                    onSave={handleSave}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default NavigationSettings;
